import type { RefObject } from 'react';

const timeoutsToClear: Record<string, NodeJS.Timeout | undefined> = {};

/**
 * Recursively resize text to fit within a container element
 * @param id - used for clearing timeouts of the right caller
 * @param containerHeight - the height of the container element that the text will be resized to fit
 * @param textRef - the text element that will be resized
 * @param resizeDirection - direction for increasing/decreasing the index of the font sizes array
 * @param fontSizes - the font sizes to iterate through
 * @param textValueRef - reference to the text value that will be resized, used to prevent recursion when the text value changes
 * @param textValueOfRecursionRoot - the text value that the recursion started with, used to prevent recursion when the text value changes
 * @param timersToClear - is used to called all the setTimeouts throwout the recursive tree (so that they can be cleared)
 * @param numberOfRetriesLeft - is used to prevent endless recursive calls in case the text height is always 0
 * @param onUpdateFontSize - callback to update the font size to a certain value
 * @param onFinalFontSizeUpdate - callback that is called once the final font size has been reached
 */
export const resizeText = ({
  id,
  containerHeight,
  textRef,
  resizeDirection = undefined,
  fontSizes,
  textValueRef,
  textValueOfRecursionRoot,
  numberOfRetriesLeft = 5,
  onUpdateFontSize,
  onFinalFontSizeUpdate,
}: {
  id: string;
  containerHeight: number;
  textRef: RefObject<HTMLSpanElement | null>;
  resizeDirection?: 'higherIndex' | 'lowerIndex' | undefined;
  fontSizes: string[];
  textValueRef: RefObject<string>;
  textValueOfRecursionRoot: string | null;
  numberOfRetriesLeft?: number;
  onUpdateFontSize: (fontSize: string) => void;
  onFinalFontSizeUpdate: (fontSize: string) => void;
}) => {
  if (!textRef.current) {
    return;
  }

  const hasTextValueChanged = textValueRef.current !== textValueOfRecursionRoot;
  if (hasTextValueChanged) {
    // if the text value has changed we want to stop the entire recursion tree from running
    // so that we have only one recursion tree running at a time (and avoid race conditions)
    return;
  }

  const textRefHeight = textRef.current.getBoundingClientRect().height;

  if (containerHeight === 0) {
    return;
  }
  if (textRefHeight === 0) {
    if (numberOfRetriesLeft <= 0) {
      return;
    }

    clearTimeout(timeoutsToClear[id]);
    // the text component might take time to render, try again soon
    timeoutsToClear[id] = setTimeout(
      () =>
        resizeText({
          id,
          containerHeight,
          textRef,
          resizeDirection,
          fontSizes,
          textValueRef,
          textValueOfRecursionRoot,
          numberOfRetriesLeft: numberOfRetriesLeft - 1,
          onUpdateFontSize,
          onFinalFontSizeUpdate,
        }),
      10,
    );
    return;
  }

  // calculate the difference between the text height and the container height
  const diff = textRefHeight - containerHeight;
  const newResizeDirection = diff > 0 ? 'lowerIndex' : 'higherIndex';

  // Get the current font size
  const currentFontSize = textRef.current.style.fontSize;
  const currentSizeIndex = fontSizes.indexOf(currentFontSize);

  if (currentSizeIndex === -1) {
    // Set the initial font size
    onUpdateFontSize(fontSizes[0]!);

    clearTimeout(timeoutsToClear[id]);
    timeoutsToClear[id] = setTimeout(
      () =>
        resizeText({
          id,
          containerHeight,
          textRef,
          resizeDirection: newResizeDirection,
          fontSizes,
          textValueRef,
          textValueOfRecursionRoot,
          onUpdateFontSize,
          onFinalFontSizeUpdate,
        }),
      0,
    );
    return;
  }

  // Check if we have reached the smallest or largest font size
  const isSmallestSize =
    currentSizeIndex === 0 && newResizeDirection === 'lowerIndex';
  const isLargestSize =
    currentSizeIndex === fontSizes.length - 1 &&
    newResizeDirection === 'higherIndex';

  // Check if the direction has changed to prevent infinite loops
  const isDirectionDifferent =
    resizeDirection !== newResizeDirection && resizeDirection !== undefined;

  const errorMargin = containerHeight * 0.05; // 5% margin of error
  const hasReachedPerfectFit = diff > -errorMargin && diff <= 0; // Allow for a small margin of error if the text is slightly smaller than the container

  const shouldStopIterating =
    isSmallestSize ||
    isLargestSize ||
    isDirectionDifferent ||
    hasReachedPerfectFit ||
    textValueRef.current === '';

  if (shouldStopIterating) {
    let finalFontSize = currentFontSize;

    if (
      newResizeDirection === 'lowerIndex' &&
      isDirectionDifferent &&
      !isSmallestSize
    ) {
      // If we have reached the end of the iterations and the text is too big,
      // go back to the previous (smaller) font size
      finalFontSize = fontSizes[currentSizeIndex - 1]!;
    }

    onUpdateFontSize(finalFontSize);
    onFinalFontSizeUpdate(finalFontSize);

    return;
  }

  if (newResizeDirection === 'lowerIndex') {
    const newSize = fontSizes[currentSizeIndex - 1]!;
    onUpdateFontSize(newSize);
  }

  if (newResizeDirection === 'higherIndex') {
    const newSize = fontSizes[currentSizeIndex + 1]!;
    onUpdateFontSize(newSize);
  }

  clearTimeout(timeoutsToClear[id]);
  timeoutsToClear[id] = setTimeout(
    () =>
      resizeText({
        id,
        containerHeight,
        textRef,
        resizeDirection: newResizeDirection,
        fontSizes,
        textValueRef,
        textValueOfRecursionRoot,
        onUpdateFontSize,
        onFinalFontSizeUpdate,
      }),
    0,
  );

  return;
};
